
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { Calendar, ChevronDown, X, RefreshCw, Check, Users, Target } from 'lucide-react';

interface FilterBarProps {
  onFilterChange: (filters: any) => void;
  onClearFilters: () => void;
  filters: any;
  isLoading?: boolean;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  onFilterChange,
  onClearFilters,
  filters,
  isLoading
}) => {
  const [isProgramOpen, setIsProgramOpen] = React.useState(false);
  const [isAudienceOpen, setIsAudienceOpen] = React.useState(false);

  const { data: programs } = useQuery({
    queryKey: ['programs-list'],
    queryFn: async () => (await supabase.from('programs').select('id, name')).data || []
  });

  const { data: audiences } = useQuery({
    queryKey: ['audiences-list'],
    queryFn: async () => (await supabase.from('audience_types').select('id, label')).data || []
  });

  const toggleProgram = (id: string) => {
    const current = filters.programIds || [];
    const next = current.includes(id) 
      ? current.filter((p: string) => p !== id)
      : [...current, id];
    onFilterChange({ programIds: next });
  };

  const toggleAudience = (id: string) => {
    const current = filters.audienceIds || [];
    const next = current.includes(id) 
      ? current.filter((a: string) => a !== id)
      : [...current, id];
    onFilterChange({ audienceIds: next });
  };

  const selectedProgramNames = programs
    ?.filter(p => filters.programIds?.includes(p.id))
    .map(p => p.name)
    .join(', ');

  const selectedAudienceLabels = audiences
    ?.filter(a => filters.audienceIds?.includes(a.id))
    .map(a => a.label)
    .join(', ');

  return (
    <div className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm">
      <div className="flex flex-wrap items-center gap-4">
        {/* Date Range Group */}
        <div className="flex items-center gap-2 bg-slate-50 p-1 rounded-lg border border-slate-100">
          <div className="flex items-center gap-2 px-3 py-1.5">
            <Calendar size={14} className="text-slate-400" />
            <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Period</span>
          </div>
          <input 
            type="date" 
            className="bg-white px-3 py-1.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-[11px] font-black uppercase text-slate-900"
            value={filters.startDate || ''}
            onChange={(e) => onFilterChange({ startDate: e.target.value })}
          />
          <span className="text-slate-300">—</span>
          <input 
            type="date" 
            className="bg-white px-3 py-1.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-[11px] font-black uppercase text-slate-900"
            value={filters.endDate || ''}
            onChange={(e) => onFilterChange({ endDate: e.target.value })}
          />
        </div>

        {/* Audience Filter */}
        <div className="relative">
          <button 
            onClick={() => setIsAudienceOpen(!isAudienceOpen)}
            className="flex items-center gap-3 px-4 py-2.5 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition-all min-w-[180px] group"
          >
            <Users size={14} className="text-slate-400 group-hover:text-slate-600" />
            <span className="text-[11px] font-black uppercase tracking-tight text-slate-900 truncate flex-1 text-left">
              {filters.audienceIds?.length > 0 ? `${filters.audienceIds.length} Audiences` : 'All Audiences'}
            </span>
            <ChevronDown size={14} className="text-slate-400" />
          </button>

          {isAudienceOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setIsAudienceOpen(false)} />
              <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-slate-200 rounded-xl shadow-xl z-20 max-h-80 overflow-auto p-2 animate-in fade-in zoom-in-95 duration-100">
                <button 
                  onClick={() => {
                    onFilterChange({ audienceIds: [] });
                    setIsAudienceOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 text-[10px] font-black uppercase hover:bg-slate-50 rounded-lg transition-all mb-1 flex items-center justify-between"
                >
                  Clear Selection
                  {(!filters.audienceIds || filters.audienceIds.length === 0) && <Check size={12} />}
                </button>
                <div className="h-px bg-slate-100 my-2" />
                {audiences?.map(a => (
                  <button 
                    key={a.id}
                    onClick={() => toggleAudience(a.id)}
                    className={`w-full text-left px-3 py-2 text-[10px] font-black uppercase rounded-lg transition-all flex items-center justify-between mb-0.5 ${filters.audienceIds?.includes(a.id) ? 'bg-blue-50 text-blue-600' : 'hover:bg-slate-50 text-slate-600'}`}
                  >
                    {a.label}
                    {filters.audienceIds?.includes(a.id) && <Check size={12} />}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Program Filter */}
        <div className="relative">
          <button 
            onClick={() => setIsProgramOpen(!isProgramOpen)}
            className="flex items-center gap-3 px-4 py-2.5 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition-all min-w-[180px] group"
          >
            <Target size={14} className="text-slate-400 group-hover:text-slate-600" />
            <span className="text-[11px] font-black uppercase tracking-tight text-slate-900 truncate flex-1 text-left">
              {filters.programIds?.length > 0 ? `${filters.programIds.length} Programs` : 'All Programs'}
            </span>
            <ChevronDown size={14} className="text-slate-400" />
          </button>

          {isProgramOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setIsProgramOpen(false)} />
              <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-slate-200 rounded-xl shadow-xl z-20 max-h-80 overflow-auto p-2 animate-in fade-in zoom-in-95 duration-100">
                <button 
                  onClick={() => {
                    onFilterChange({ programIds: [] });
                    setIsProgramOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 text-[10px] font-black uppercase hover:bg-slate-50 rounded-lg transition-all mb-1 flex items-center justify-between"
                >
                  Clear Selection
                  {(!filters.programIds || filters.programIds.length === 0) && <Check size={12} />}
                </button>
                <div className="h-px bg-slate-100 my-2" />
                {programs?.map(p => (
                  <button 
                    key={p.id}
                    onClick={() => toggleProgram(p.id)}
                    className={`w-full text-left px-3 py-2 text-[10px] font-black uppercase rounded-lg transition-all flex items-center justify-between mb-0.5 ${filters.programIds?.includes(p.id) ? 'bg-blue-50 text-blue-600' : 'hover:bg-slate-50 text-slate-600'}`}
                  >
                    {p.name}
                    {filters.programIds?.includes(p.id) && <Check size={12} />}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="flex-1" />

        {isLoading && (
          <RefreshCw size={16} className="animate-spin text-slate-400" />
        )}
        
        <button 
          onClick={onClearFilters}
          className="px-4 py-2 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-rose-500 transition-colors"
        >
          Reset Filters
        </button>
      </div>
    </div>
  );
};
